// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
/** Canonical wire-encoding index for TowerType. Same rules as UNIT_TYPE_INDEX. */
export const TOWER_TYPE_INDEX = [
    "arrow", "ballista", "cannon", "arbalest", "harpoon", "mortar",
    "polybolos", "skyhook", "howitzer",
    "skorpion", "stormpike", "culverin",
    "manticore", "skybreaker", "bombard",
];
export const TOWER_TYPE_TO_INDEX = Object.freeze(Object.fromEntries(TOWER_TYPE_INDEX.map((t, i) => [t, i])));
/**
 * Canonical wire-encoding index for UnitType. Used by the binary codec
 * (`encodeSnapshot`/`decodeSnapshot`) — order is stable on the wire.
 *
 * The `satisfies readonly UnitType[]` assertion turns "added a UnitType but
 * forgot to index it" into a TS compile error rather than a runtime bug.
 */
export const UNIT_TYPE_INDEX = [
    "chicken", "peasant", "militia", "elder", "footmen",
    "squire", "soldier", "lancer", "skyLancer", "juggernaut", "raider",
    "duelist", "berserker", "paladin", "griffinRider",
    "minotaurs", "behemoth", "wyvern", "warGolem",
    "shinigami", "dragon", "titan", "colossus",
];
/** Reverse lookup for the encoder. Built once at module load. */
export const UNIT_TYPE_TO_INDEX = Object.freeze(Object.fromEntries(UNIT_TYPE_INDEX.map((t, i) => [t, i])));
/** Stable order — the binary encoder maps these to byte indices. Append only. */
export const FACTION_IDS = ["nature", "ice", "knight"];
// ---------------------------------------------------------------------------
// Delta application (client + parity tests)
// ---------------------------------------------------------------------------
/**
 * Apply a `SnapshotDeltaMessage` to the previous full snapshot, returning a
 * new full snapshot. Pure function — does not mutate `prev`. Apply order
 * within each player is `Removed → Added → Updated` so that an id reused on
 * the same tick (very rare) is handled deterministically.
 */
export function applySnapshotDelta(prev, delta) {
    const playersById = new Map(prev.players.map((p) => [p.playerId, p]));
    const players = [];
    for (const prevPlayer of prev.players) {
        const dp = delta.players.find((d) => d.playerId === prevPlayer.playerId);
        if (dp === undefined) {
            players.push(prevPlayer);
            continue;
        }
        players.push(applyPlayerDelta(prevPlayer, dp));
    }
    for (const dp of delta.players) {
        if (!playersById.has(dp.playerId)) {
            // Player added mid-match. Not currently emitted — match composition is
            // fixed — but defined for forward-compat.
            players.push(applyPlayerDelta(emptyPlayerSnapshot(dp.playerId), dp));
        }
    }
    return {
        type: "snapshot",
        tick: delta.tick,
        attacks: delta.attacks,
        unitAttacks: delta.unitAttacks,
        splashShots: delta.splashShots,
        splashImpacts: delta.splashImpacts,
        players,
    };
}
function emptyPlayerSnapshot(playerId) {
    return {
        playerId,
        displayName: "",
        gold: 0,
        income: 0,
        lives: 0,
        techPoints: 0,
        laneExpansionLevel: 0,
        techOwned: [],
        factionPicks: { t3: null, t4: null, t5: null },
        towers: [],
        units: [],
        unitStockpile: {},
    };
}
function applyPlayerDelta(prev, dp) {
    const towers = applyTowerDeltas(prev.towers, dp);
    const units = applyUnitDeltas(prev.units, dp);
    const stockpile = dp.stockpileChanged === undefined
        ? prev.unitStockpile
        : { ...prev.unitStockpile, ...dp.stockpileChanged };
    const next = {
        playerId: prev.playerId,
        displayName: prev.displayName,
        gold: dp.gold ?? prev.gold,
        income: dp.income ?? prev.income,
        lives: dp.lives ?? prev.lives,
        techPoints: dp.techPoints ?? prev.techPoints,
        laneExpansionLevel: dp.laneExpansionLevel ?? prev.laneExpansionLevel,
        techOwned: dp.techOwned ?? prev.techOwned,
        factionPicks: dp.factionPicks ?? prev.factionPicks,
        towers,
        units,
        unitStockpile: stockpile,
    };
    if (prev.isBot === true)
        next.isBot = true;
    if (dp.hasLeft === true || prev.hasLeft === true)
        next.hasLeft = true;
    return next;
}
function applyTowerDeltas(prev, dp) {
    if (dp.towersAdded === undefined && dp.towersUpdated === undefined && dp.towersRemoved === undefined) {
        return prev;
    }
    const removed = dp.towersRemoved !== undefined ? new Set(dp.towersRemoved) : null;
    const updates = dp.towersUpdated !== undefined ? new Map(dp.towersUpdated.map((t) => [t.id, t])) : null;
    const out = [];
    for (const t of prev) {
        if (removed?.has(t.id) === true)
            continue;
        const u = updates?.get(t.id);
        if (u === undefined) {
            out.push(t);
        }
        else {
            out.push({
                id: t.id,
                type: u.type ?? t.type,
                cell: t.cell,
                hp: u.hp ?? t.hp,
                maxHp: u.maxHp ?? t.maxHp,
                buildProgress: u.buildProgress ?? t.buildProgress,
                sellProgress: u.sellProgress ?? t.sellProgress,
                goldSpent: u.goldSpent ?? t.goldSpent,
            });
        }
    }
    if (dp.towersAdded !== undefined) {
        for (const a of dp.towersAdded) {
            out.push({
                id: a.id,
                type: a.type,
                cell: a.cell,
                hp: a.hp,
                maxHp: a.maxHp,
                buildProgress: a.buildProgress,
                sellProgress: a.sellProgress ?? 0,
                goldSpent: a.goldSpent,
            });
        }
    }
    return out;
}
function applyUnitDeltas(prev, dp) {
    if (dp.unitsAdded === undefined && dp.unitsUpdated === undefined && dp.unitsRemoved === undefined) {
        return prev;
    }
    const removed = dp.unitsRemoved !== undefined ? new Set(dp.unitsRemoved) : null;
    const updates = dp.unitsUpdated !== undefined ? new Map(dp.unitsUpdated.map((u) => [u.id, u])) : null;
    const out = [];
    for (const u of prev) {
        if (removed?.has(u.id) === true)
            continue;
        const upd = updates?.get(u.id);
        if (upd === undefined) {
            out.push(u);
        }
        else {
            out.push({
                id: u.id,
                type: u.type,
                hp: upd.hp ?? u.hp,
                maxHp: u.maxHp,
                armor: u.armor,
                x: upd.x ?? u.x,
                y: upd.y ?? u.y,
                vx: upd.vx ?? u.vx,
                vy: upd.vy ?? u.vy,
                ownerId: u.ownerId,
            });
        }
    }
    if (dp.unitsAdded !== undefined) {
        for (const a of dp.unitsAdded) {
            out.push({
                id: a.id,
                type: a.type,
                hp: a.hp,
                maxHp: a.maxHp,
                armor: a.armor,
                x: a.x,
                y: a.y,
                vx: a.vx,
                vy: a.vy,
                ownerId: a.ownerId,
            });
        }
    }
    return out;
}
// Binary codec for snapshot/snapshot_delta wire format. Re-exported here so
// `import { encodeSnapshot, decodeSnapshot } from "@towerhold/protocol"` works.
// Cyclical import is safe: binary.ts references UNIT_TYPE_INDEX /
// TOWER_TYPE_INDEX only from inside function bodies, and those are evaluated
// before this re-export runs.
export { encodeSnapshot, decodeSnapshot } from "./binary.js";
